import * as Sentry from "@sentry/nextjs";

/**
 * Where customers are lost, recorded at the moment it happens.
 *
 * Every investigation into the conversion drop hit the same wall: the failures
 * are invisible, so they can only be inferred from absence. Photos vanished
 * with no trace because they only ever existed in memory. Rejected coupons
 * left nothing behind. Refused 4xx were suppressed by the backend filter.
 * Picker rejections were discarded unread. In each case the database showed a
 * customer who "just didn't continue", which is indistinguishable from one who
 * tried and was blocked.
 *
 * Two primitives, deliberately different in cost:
 *
 * - `trackStep` writes a Sentry BREADCRUMB. Breadcrumbs create no issues and
 *   cost nothing, but they ride along on any error that happens later — so
 *   when something does break, the report carries the customer's whole journey
 *   instead of one stack trace with no context.
 * - `trackLoss` writes an actual event, and is reserved for moments where a
 *   customer demonstrably lost something or was refused. These are meant to be
 *   few. Reporting every harmless step here is what buried the real signal
 *   last time (51 identical events in three days pushed a real issue out of
 *   view), so the bar is: would you want to be woken up by a hundred of these?
 *
 * Both swallow their own errors. Instrumentation must never be the reason a
 * customer cannot check out.
 */

/** Points on the path to a completed order. Add sparingly and keep them stable. */
export type FunnelStep =
  | "picker-opened"
  | "files-picked"
  | "files-rejected"
  | "thumbnails-ready"
  | "upload-started"
  | "upload-finished"
  | "checkout-opened"
  | "coupon-submitted"
  | "credit-submitted"
  | "payment-started"
  // Card / invitation / menu editor (design page).
  | "editor-opened"
  | "template-chosen"
  | "card-added-to-cart"
  // Calendar builder.
  | "calendar-opened"
  | "calendar-format-chosen"
  | "calendar-first-photo"
  | "calendar-add-to-cart-clicked"
  | "calendar-added-to-cart"
  // Calendar builder v2: the IndexedDB draft could not be written (quota,
  // iOS Blob limits). The builder carries on; only the draft is behind.
  | "calendar-draft-save-failed"
  // Calendar builder v2 steps.
  | "calendar-v2-photos-picked"
  | "calendar-v2-design-changed"
  | "calendar-v2-month-edited"
  | "calendar-v2-options-opened"
  // AR wall preview of an enlargement (povečave editor, /ar-preview links).
  | "ar-preview-opened"
  | "ar-started"
  | "ar-failed"
  | "ar-unsupported";

/** Moments where the customer lost something or was turned away. */
export type FunnelLoss =
  | "selection-cleared"
  | "coupon-rejected"
  // A customer was told by email that they had money here and then turned
  // away from it. Same bar as coupon-rejected, and the claim token lives ten
  // minutes, so "how many people are arriving after their window closed" is a
  // question this is the only way to answer.
  | "credit-rejected"
  | "upload-failed"
  | "checkout-blocked"
  // A finished card/invitation design that could not be put in the cart —
  // render or backend refusal. The design survives as a local draft, but the
  // customer was turned away at the last step.
  | "card-add-to-cart-failed"
  // Same for a finished calendar; the draft survives in IndexedDB.
  | "calendar-add-to-cart-failed";

type Detail = Record<string, unknown>;

/**
 * A step on the journey. Free: becomes a breadcrumb, not an issue.
 * Call these liberally — the whole point is that the trail exists before
 * anything goes wrong.
 */
export function trackStep(step: FunnelStep, detail: Detail = {}): void {
  try {
    Sentry.addBreadcrumb({
      category: "funnel",
      message: step,
      level: "info",
      data: detail,
    });
  } catch {
    // Never take the checkout down for a breadcrumb.
  }
}

/**
 * A customer lost something. Becomes a real event, so keep these rare and
 * meaningful — the `reason` is the grouping key, so it must describe the cause
 * and not the symptom.
 *
 * Tagged rather than only logged, so "how many customers hit an expired coupon
 * today" is a Sentry filter rather than a database excavation.
 */
export function trackLoss(loss: FunnelLoss, detail: Detail = {}): void {
  try {
    const tags: Record<string, string> = { area: "funnel", loss };
    // Promote short scalar details to tags so they can be filtered on. Long or
    // structured values stay in `extra` where they do not explode cardinality.
    for (const [key, value] of Object.entries(detail)) {
      if (
        (typeof value === "string" && value.length <= 40) ||
        typeof value === "number" ||
        typeof value === "boolean"
      ) {
        tags[key] = String(value);
      }
    }

    Sentry.captureMessage(`Funnel loss: ${loss}`, {
      level: "warning",
      tags,
      extra: detail,
    });
  } catch {
    // As above.
  }
}
